/**
 * Cut / Copy / Paste of the document's selection as menu entries. The editor's right-click menu
 * (editormenu.ts) shows them, and so does a formula's own menu (nodeviews/math.ts) when the formula
 * lies inside the selection — an equation selected whole, or text dragged across one — so that the
 * selection can be cut or copied from wherever one right-clicks on it.
 */
import type { EditorView } from 'prosemirror-view';
import type { Selection } from 'prosemirror-state';
import type { MenuItem } from './contextmenu';
import { editorContext } from './context';
import { insertImageFiles } from './imagepaste';

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
export const MOD = isMac ? '⌘' : 'Ctrl';

/** the node at `pos` (of `size` positions) lies wholly inside a non-empty selection */
export function selectionCovers(sel: Selection, pos: number, size: number): boolean {
  return !sel.empty && sel.from <= pos && sel.to >= pos + size;
}

/** What the async clipboard API holds: images, the HTML and the plain text ('' when absent). */
export async function readClipboard(): Promise<{ images: File[]; html: string; text: string }> {
  const out = { images: [] as File[], html: '', text: '' };
  for (const item of await navigator.clipboard?.read?.() ?? []) {
    // prefer the vector form when both are offered (Chromium 124+ carries image/svg+xml)
    const image = item.types.find(t => t === 'image/svg+xml') ?? item.types.find(t => t.startsWith('image/'));
    if (image) out.images.push(new File([await item.getType(image)], 'image', { type: image }));
    if (!out.html && item.types.includes('text/html')) out.html = await (await item.getType('text/html')).text();
    if (!out.text && item.types.includes('text/plain')) out.text = await (await item.getType('text/plain')).text();
  }
  return out;
}

/**
 * A paste event carrying `data`. Firefox's ClipboardEvent constructor leaves clipboardData empty
 * whatever the init says (the menu's Paste of LaTeX text went in as raw text there, not parsed),
 * so the data is set on the event itself where the browser did not take it.
 */
export function pasteEventWith(data: DataTransfer): ClipboardEvent {
  const event = new ClipboardEvent('paste', { clipboardData: data });
  if (event.clipboardData !== data) Object.defineProperty(event, 'clipboardData', { value: data });
  return event;
}

/**
 * Paste from a menu or the toolbar (no paste event, so the async clipboard is read): images become
 * graphics insets; everything else takes the same way as Ctrl+V — the HTML an OverLyX copy wrote
 * keeps its insets, and table cells copied from a table (whole rows too) go in cell by cell from
 * the cursor's cell (prosemirror-tables, as LyX pastes a tabular selection); plain text is split
 * into paragraphs, LaTeX in it parsed. False when the clipboard could not be read.
 */
export async function pasteFromClipboard(view: EditorView): Promise<boolean> {
  let clip: { images: File[]; html: string; text: string };
  try { clip = await readClipboard(); } catch {
    // no clipboard.read (older Safari / Firefox, some webviews): the text alone
    try { clip = { images: [], html: '', text: await navigator.clipboard?.readText?.() ?? '' }; } catch { return false; }
  }
  if (view.isDestroyed) return false;
  if (clip.images.length) { await insertImageFiles(view, clip.images); return true; }
  if (!clip.html && !clip.text) return true;   // an empty clipboard: nothing to paste
  view.focus();
  // the editor's handlePaste reads clipboardData (LaTeX, SVG markup …): hand it the same data
  let event: ClipboardEvent | undefined;
  try {
    const data = new DataTransfer();
    if (clip.text) data.setData('text/plain', clip.text);
    if (clip.html) data.setData('text/html', clip.html);
    event = pasteEventWith(data);
  } catch { /* no DataTransfer constructor: ProseMirror makes an empty event */ }
  return clip.html ? view.pasteHTML(clip.html, event) : view.pasteText(clip.text, event);
}

/** Paste the clipboard's text as plain text: no formatting, no LaTeX parsed (Google Docs' "Paste without formatting"). */
export async function pastePlainFromClipboard(view: EditorView): Promise<boolean> {
  let text: string;
  try { text = await navigator.clipboard?.readText?.() ?? ''; } catch { return false; }
  if (view.isDestroyed) return false;
  view.focus();
  if (text) view.pasteText(text);
  return true;
}

/**
 * Cut and Copy (disabled without a selection), Paste (images become graphics insets, text and
 * table cells go in as with Ctrl+V), Paste without formatting, and Delete (of the selection).
 */
export function clipboardMenuItems(view: EditorView): MenuItem[] {
  const hasSel = !view.state.selection.empty;
  const failed = (keys: string) => () => editorContext.notify?.('Use ' + keys + ' to paste', 'error');
  return [
    { label: 'Cut', icon: 'cut', shortcut: MOD + '+X', disabled: !hasSel, action: () => { view.focus(); document.execCommand('cut'); } },
    { label: 'Copy', icon: 'copy', shortcut: MOD + '+C', disabled: !hasSel, action: () => { view.focus(); document.execCommand('copy'); } },
    { label: 'Paste', icon: 'paste', shortcut: MOD + '+V', action: () => {
      view.focus();
      pasteFromClipboard(view).then(ok => { if (!ok) failed(MOD + '+V')(); }).catch(failed(MOD + '+V'));
    } },
    { label: 'Paste without formatting', icon: 'pastePlain', shortcut: MOD + '+Shift+V', action: () => {
      pastePlainFromClipboard(view).then(ok => { if (!ok) failed(MOD + '+Shift+V')(); }).catch(failed(MOD + '+Shift+V'));
    } },
    { label: 'Delete', icon: 'delete', disabled: !hasSel, action: () => { view.dispatch(view.state.tr.deleteSelection().scrollIntoView()); view.focus(); } },
  ];
}
